import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { dramas } from "@/mocks/dramas";
import { reviews } from "@/mocks/reviews";
import Breadcrumb from "@/components/feature/Breadcrumb";
import DramaCard from "@/components/base/DramaCard";
import EmptyState from "@/components/feature/EmptyState";
import ReviewArticle from "@/components/feature/ReviewArticle";
import RelationshipGraph from "./components/RelationshipGraph";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const drama = dramas.find((d) => d.id === id);
  const [showFullSynopsis, setShowFullSynopsis] = useState(false);

  const dramaReviews = useMemo(
    () => (drama ? reviews.filter((r) => r.relatedDramaId === drama.id) : []),
    [drama]
  );

  const related = useMemo(() => {
    if (!drama) return [];
    const scored = dramas
      .filter((d) => d.id !== drama.id)
      .map((d) => ({
        drama: d,
        score: d.tags.filter((t) => drama.tags.includes(t)).length,
      }))
      .sort((a, b) => b.score - a.score || b.drama.rating - a.drama.rating);
    return scored.slice(0, 3).map((s) => s.drama);
  }, [drama]);

  useSeo({
    title: drama ? `${drama.title} - 剧情介绍与影评分析` : "作品不存在 - 出轨的妻子影视大全",
    description: drama
      ? `${drama.title}：${drama.summary.slice(0, 60)}... 含该剧导演主演、角色关系分析、专家影评与同类推荐。`
      : "抱歉，未找到对应的影视作品。",
    keywords: drama
      ? `${drama.title},${drama.genre},剧情介绍,影评,角色分析,${drama.tags.join(",")}`
      : "出轨的妻子,影视详情",
  });

  if (!drama) {
    return (
      <div className="mx-auto max-w-3xl px-4 md:px-6 py-16">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视分类", to: "/category" }, { label: "未找到" }]} />
        <div className="mt-8">
          <EmptyState
            icon="ri-error-warning-line"
            title="很抱歉，没有找到这部影视作品"
            description="它可能已被移除，或者链接有误。你可以返回分类页继续浏览其他作品。"
          />
          <div className="mt-6 flex justify-center gap-3">
            <Link
              to="/category"
              className="inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-grid-line" />
              返回影视分类
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-800 hover:border-primary-300 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-4-line" />
              回到首页
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const visibleSynopsis = showFullSynopsis ? drama.synopsis : drama.synopsis.slice(0, 1);

  return (
    <div className="pb-6">
      {/* Hero */}
      <section className="relative bg-foreground-950">
        <div className="absolute inset-0">
          <img
            src={drama.backdrop}
            alt={`${drama.title} 剧照`}
            title={`${drama.title}·剧情介绍`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-12">
          <Breadcrumb
            light
            items={[{ label: "首页", to: "/" }, { label: "影视分类", to: "/category" }, { label: drama.title }]}
          />
          <div className="mt-6 flex flex-col md:flex-row gap-6 md:gap-8">
            <div className="w-40 md:w-52 shrink-0 rounded-lg overflow-hidden border border-white/15">
              <img
                src={drama.poster}
                alt={`${drama.title} 封面`}
                title={`${drama.title}·出轨的妻子影视大全`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-white">{drama.title}</h1>
              <div className="mt-4 flex flex-wrap items-center gap-2.5 text-sm text-white/85">
                <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-medium text-white">{drama.type}</span>
                <span>{drama.year}</span>
                <span className="w-px h-3.5 bg-white/30" />
                <span>{drama.genre}</span>
                {drama.episodes > 0 && (
                  <>
                    <span className="w-px h-3.5 bg-white/30" />
                    <span>全 {drama.episodes} 集</span>
                  </>
                )}
              </div>
              <div className="mt-4 flex items-center gap-3">
                <span className="flex items-center gap-1.5 text-secondary-300">
                  <i className="ri-star-fill" />
                  <span className="font-heading text-xl font-bold">{drama.rating.toFixed(1)}</span>
                  <span className="text-xs text-white/60">/ 10</span>
                </span>
                <span className="text-xs text-white/60">人气 {drama.popularity.toLocaleString()}</span>
              </div>
              <p className="mt-5 text-sm leading-7 text-white/80 max-w-2xl">{drama.summary}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {drama.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/category?tag=${encodeURIComponent(tag)}`}
                    className="rounded-full bg-white/10 border border-white/20 px-3 py-1 text-xs text-white/90 hover:bg-white/25 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 md:px-6 mt-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-12">
            {/* Synopsis */}
            <section>
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2.5">
                <span className="w-1 h-5 rounded-full bg-accent-500" />
                剧情简介
              </h2>
              <div className="space-y-4 text-sm md:text-base leading-8 text-foreground-700">
                {visibleSynopsis.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              {drama.synopsis.length > 1 && (
                <button
                  type="button"
                  onClick={() => setShowFullSynopsis((v) => !v)}
                  className="mt-4 inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {showFullSynopsis ? "收起剧情介绍" : "阅读完整剧情介绍"}
                  <i className={showFullSynopsis ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
                </button>
              )}
            </section>

            {/* Characters */}
            <section>
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2.5">
                <span className="w-1 h-5 rounded-full bg-accent-500" />
                角色介绍
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {drama.characters.map((c) => (
                  <div key={c.name} className="flex gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                    <span className="shrink-0 w-11 h-11 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-800 font-heading font-bold">
                      {c.name.slice(0, 1)}
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-heading text-base font-semibold text-foreground-950">{c.name}</h3>
                        <span className="rounded-full bg-background-200 px-2 py-0.5 text-[11px] text-foreground-600">{c.role}</span>
                      </div>
                      <p className="mt-0.5 text-xs text-foreground-500">演员：{c.actor}</p>
                      <p className="mt-1.5 text-sm leading-6 text-foreground-600">{c.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Relationship graph */}
            <section>
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2.5">
                <span className="w-1 h-5 rounded-full bg-accent-500" />
                角色关系图
              </h2>
              <RelationshipGraph characters={drama.characters} relations={drama.relations} />
            </section>

            {/* Expert reviews */}
            <section>
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2.5">
                <span className="w-1 h-5 rounded-full bg-accent-500" />
                专家影评
              </h2>
              {dramaReviews.length > 0 ? (
                <div className="space-y-5">
                  {dramaReviews.map((review) => (
                    <ReviewArticle key={review.id} review={review} anchorId={review.id} />
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon="ri-quill-pen-line"
                  title="该作品暂无专家影评"
                  description="我们的影评团队正在撰写中。你可以先浏览影评专题页的其他深度解读。"
                />
              )}
            </section>
          </div>

          {/* Sidebar */}
          <aside className="space-y-8">
            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-information-line text-primary-500" />
                基本信息
              </h2>
              <dl className="space-y-3 text-sm">
                {[
                  { label: "类型", value: drama.type },
                  { label: "导演", value: drama.director },
                  { label: "主演", value: drama.cast.join("、") },
                  { label: "题材", value: drama.genre },
                  { label: "上映时间", value: `${drama.year} 年` },
                  { label: "集数", value: drama.episodes > 0 ? `全 ${drama.episodes} 集` : "电影长片" },
                  { label: "评分", value: `${drama.rating.toFixed(1)} 分` },
                ].map((row) => (
                  <div key={row.label} className="flex gap-3">
                    <dt className="shrink-0 w-16 text-foreground-500">{row.label}</dt>
                    <dd className="text-foreground-800 flex-1">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-compass-3-line text-primary-500" />
                相关推荐
              </h2>
              {related.length > 0 ? (
                <ul className="space-y-3">
                  {related.map((d) => (
                    <li key={d.id}>
                      <Link to={`/detail/${d.id}`} className="group flex gap-3 cursor-pointer">
                        <div className="w-12 shrink-0 rounded overflow-hidden bg-background-200" style={{ height: 66 }}>
                          <img
                            src={d.poster}
                            alt={`${d.title} 封面`}
                            title={`${d.title}·出轨的妻子影视大全`}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">{d.title}</h3>
                          <p className="mt-0.5 text-xs text-foreground-500">{d.type} · {d.year}</p>
                          <p className="mt-1.5 text-xs text-foreground-600 line-clamp-2">{d.summary}</p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-foreground-500">暂无相关推荐</p>
              )}
            </section>

            <Link
              to="/category"
              className="flex items-center justify-center gap-1.5 rounded-md border border-background-300 px-4 py-3 text-sm font-medium text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-grid-line" />
              返回影视分类
            </Link>
          </aside>
        </div>

        {/* Related grid */}
        {related.length > 0 && (
          <section className="mt-14">
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6 flex items-center gap-2.5">
              <span className="w-1 h-5 rounded-full bg-accent-500" />
              同类题材影视推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-5">
              {related.map((d) => (
                <DramaCard key={d.id} drama={d} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}